/**
 * 생육 3D 분석 — 장비 사진과 grosync-lrm 이 만든 3D 모델을 날짜별로 함께 본다.
 *
 * 자료는 생육 사진 옆 파일({YYYYMMDD}.json · _height.glb · _leaf.glb · .glb, `./api.ts`)이다.
 * 화면은 grosync-local 관리자의 3D 분석 탭을 옮긴 것 — 관리자 디자인 체계(Panel / Sym / m3 색 토큰)를 쓴다.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DeviceInfo, FarmInfo, GrowthRecord } from '@grosync/common';
import { Panel, Sym, SectionLabel } from '../../ui';
import { LoadingSpinner, PageError } from '../../components/LoadingSpinner';
import { getPhotoUrl } from '../../api/growth';
import {
  downloadAuthed,
  loadModelViewer,
  preloadPhoto,
  siblingUrl,
  useAuthedUrl,
  type Growth3dResult,
  type Growth3dView,
  type MeasureHotspot,
} from './api';
import { Growth3dTrends, useDailyTemp } from './Trends';
import { CalendarPopup, ComparePanel, ToolBtn, exportCsv } from './Tools';
import { UploadPhotoPopup } from './Upload';
import { lrmPhase, lrmProgressLabel, lrmProgressShort } from './progress';
import { GrowthDeviceSelect } from '../growth/components/GrowthDeviceSelect';
import { lrmRegistrationNeeded, type ManualUploadResult } from '../../api/growth';
import { LrmRegistrationNotice } from '../growth/components/LrmRegistrationNotice';
import { useQueryClient } from '@tanstack/react-query';
import { useDeviceShots, withinDays } from './shots';

/* ---- 3D 뷰어 ----------------------------------------------------------- */

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      // model-viewer 는 커스텀 엘리먼트다 — 속성은 문자열로 넘긴다.
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> &
        Record<string, unknown>;
    }
  }
}

/** model-viewer 에서 이 화면이 쓰는 부분만 */
interface ModelViewerEl extends HTMLElement {
  loaded?: boolean;
  model?: {
    materials: {
      name: string;
      setAlphaMode: (mode: 'OPAQUE' | 'BLEND' | 'MASK') => void;
      pbrMetallicRoughness: {
        baseColorFactor: number[];
        setBaseColorFactor: (rgba: number[]) => void;
      };
    }[];
  };
  getFieldOfView?: () => number;
  fieldOfView?: string;
  maxFieldOfView?: string;
  minFieldOfView?: string;
  jumpCameraToGoal?: () => void;
}

const rgb = (c: number[]): string => `rgb(${c.join(',')})`;

function ModelStage({
  src,
  loadError,
  processed,
  pendingText,
  views,
  viewKey,
  onViewChange,
  label,
  cameraOrbit,
  cameraTarget,
  fieldOfView,
  measureHotspots,
  ghostDefault,
  ghostAlpha,
  switching,
}: {
  /** 보기 GLB 의 blob URL. 받는 중이면 null */
  src: string | null;
  loadError: string | null;
  /** 같은 날짜의 다른 보기를 받는 중 — 그동안 앞 보기를 그대로 띄워 둔다 */
  switching: boolean;
  /** 그날 결과 json 이 있는지 — 없으면 pendingText (처리 단계) */
  processed: boolean;
  /** 결과가 없을 때 문구 — '3D 처리 전' · '3D 처리 중' · '3D 처리 실패 — 사유' 등 (progress.ts) */
  pendingText?: string;
  views: Growth3dView[];
  viewKey?: string;
  onViewChange: (key: string) => void;
  label: string;
  /** 첫 시점 — 사진 찍은 카메라 방향 (`0deg 67deg 0.5m`) */
  cameraOrbit?: string;
  /** 첫 화면 중심 — 포기 가운데 */
  cameraTarget?: string;
  /** 첫 화면 시야각 — 사진 카메라의 세로 시야각. 같이 오면 첫 화면이 사진과 같은 배율 */
  fieldOfView?: string;
  measureHotspots: MeasureHotspot[];
  ghostDefault: boolean;
  ghostAlpha: number;
}): React.ReactElement {
  const [ready, setReady] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  // 잎 반투명 — 모든 보기(초장 · 엽면적)에서 켜고 끌 수 있다. 처음 상태는 보기 정의(ghost_default)를 따르고,
  // 보기를 바꾸면 그 보기의 처음 상태로 돌아간다 (초장 = 끔 · 엽면적 = 켬)
  const [ghost, setGhost] = useState(ghostDefault);
  useEffect(() => {
    setGhost(ghostDefault);
  }, [ghostDefault, viewKey]);
  const mvRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let alive = true;
    loadModelViewer().then(
      () => alive && setReady(true),
      (e: Error) => alive && setViewerError(e.message),
    );
    return () => {
      alive = false;
    };
  }, []);

  // 잎 반투명 — 재질 'plant' = 생성 포기 · 'overlay' = 측정선·라벨 (LRM compress_glb_for_web 가 나눈다)
  useEffect(() => {
    const mv = mvRef.current as ModelViewerEl | null;
    if (!mv) return;
    const on = ghost;
    const apply = (): void => {
      for (const m of mv.model?.materials ?? []) {
        if (m.name !== 'plant') continue;
        const c = m.pbrMetallicRoughness.baseColorFactor;
        m.setAlphaMode(on ? 'BLEND' : 'OPAQUE');
        m.pbrMetallicRoughness.setBaseColorFactor([c[0], c[1], c[2], on ? ghostAlpha : 1]);
      }
    };
    if (mv.model) apply();
    mv.addEventListener('load', apply);
    return () => mv.removeEventListener('load', apply);
  }, [ghost, ghostAlpha, ready, src]);

  // 사진 시야각 그대로 — model-viewer 는 모델이 옆으로 넓으면 준 시야각을 스스로 넓힌다
  // (tan 에 max(1, 모델 가로세로비/화면 가로세로비) 를 곱함). 불러온 뒤 실제 적용된 값을 읽어 그만큼 되돌린다.
  useEffect(() => {
    const mv = mvRef.current as ModelViewerEl | null;
    if (!mv || !fieldOfView) return;
    const want = parseFloat(fieldOfView);
    const fix = (): void => {
      const got = mv.getFieldOfView?.();
      if (!want || !got) return;
      const t = (d: number): number => Math.tan(((d / 2) * Math.PI) / 180);
      const k = t(got) / t(want);
      if (Math.abs(k - 1) < 0.005) return;
      const deg = `${(2 * Math.atan(t(want) / k) * 180) / Math.PI}deg`;
      mv.minFieldOfView = '1deg';
      mv.maxFieldOfView = deg;
      mv.fieldOfView = deg;
      mv.jumpCameraToGoal?.();
    };
    if (mv.loaded) fix();
    mv.addEventListener('load', fix);
    return () => mv.removeEventListener('load', fix);
  }, [ready, src, fieldOfView]);

  const frame: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    borderRadius: 14,
    overflow: 'hidden',
    // 배경 = 촬영 사진 칸과 같은 색 (라이트/다크 모드 따라감) + 옅은 모눈 (가는 선 20 px · 굵은 선 100 px)
    // 모눈은 화면 배경 장식이다 — 3D 안의 자가 아니라 확대·회전해도 그대로
    background: 'var(--md-sys-color-surface-container)',
    backgroundImage: [
      'linear-gradient(color-mix(in srgb, var(--md-sys-color-on-surface) 10%, transparent) 1px, transparent 1px)',
      'linear-gradient(90deg, color-mix(in srgb, var(--md-sys-color-on-surface) 10%, transparent) 1px, transparent 1px)',
      'linear-gradient(color-mix(in srgb, var(--md-sys-color-on-surface) 5%, transparent) 1px, transparent 1px)',
      'linear-gradient(90deg, color-mix(in srgb, var(--md-sys-color-on-surface) 5%, transparent) 1px, transparent 1px)',
    ].join(', '),
    backgroundSize: '100px 100px, 100px 100px, 20px 20px, 20px 20px',
    backgroundPosition: 'center center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (!processed)
    return (
      <div style={frame}>
        <Empty icon={pendingText?.startsWith('3D 처리 실패') ? 'error' : 'hourglass_empty'} text={pendingText ?? '3D 처리 전'} />
      </div>
    );
  if (viewerError || loadError) {
    return (
      <div style={frame}>
        <Empty icon="error" text={viewerError ?? `3D 모델을 받지 못했습니다 (${loadError})`} />
      </div>
    );
  }
  if (!ready || !src)
    return (
      <div style={frame}>
        <Empty icon="deployed_code" text="3D 모델 불러오는 중…" />
      </div>
    );

  const pill = (on: boolean): React.CSSProperties => ({
    padding: '5px 10px',
    borderRadius: 10,
    font: '600 11px/15px var(--gs-font-kr)',
    cursor: 'pointer',
    background: on ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)',
    color: on ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface)',
    border: '1px solid var(--md-sys-color-outline-variant)',
  });

  return (
    <div style={frame}>
      <model-viewer
        ref={mvRef as React.Ref<HTMLElement>}
        // 보기(초장·엽면적)를 바꿀 때는 다시 만들지 않는다 — 돌려 둔 시점이 그대로 남는다. 날짜가 바뀌면(시점 값이 다르면) 새로
        key={`${cameraOrbit ?? ''}|${cameraTarget ?? ''}|${fieldOfView ?? ''}`}
        src={src}
        alt={label}
        camera-controls=""
        // 불러온 뒤 손 모양 안내가 모델을 좌우로 흔드는 기본 동작 — 끈다
        interaction-prompt="none"
        camera-orbit={cameraOrbit}
        camera-target={cameraTarget}
        field-of-view={fieldOfView ?? '30deg'}
        // 최대 시야각 기본(auto)은 '모델이 꽉 차는 각도'라 사진 시야각(광각)을 잘라 확대돼 보인다 — 같은 값으로 연다
        max-field-of-view={fieldOfView ?? 'auto'}
        // 사진 카메라 거리 그대로 — 최소 거리 기본(auto)이 모델 크기로 막아 멀어진다
        min-camera-orbit={fieldOfView ? 'auto auto 0.01m' : undefined}
        touch-action="pan-y"
        exposure="1.05"
        shadow-intensity="0.7"
        style={{ width: '100%', height: '100%', background: 'transparent' }}
      >
        {measureHotspots.map((h) => (
          <div
            key={h.key}
            slot={`hotspot-m-${h.key}`}
            data-position={h.position_m.map((v) => `${v}m`).join(' ')}
            data-normal={h.normal.join(' ')}
            style={
              h.style === 'text'
                ? {
                    font: '800 13px/16px var(--gs-font-kr)',
                    whiteSpace: 'nowrap',
                    color: rgb(h.rgb),
                    textShadow:
                      '0 0 2px #000, 0 0 2px #000, 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
                    pointerEvents: 'none',
                    transform:
                      h.side === 'left'
                        ? 'translateX(calc(-50% - 8px))'
                        : h.side === 'right'
                          ? 'translateX(calc(50% + 8px))'
                          : 'none',
                  }
                : {
                    padding: '2px 7px',
                    borderRadius: 8,
                    font: '700 11px/15px var(--gs-font-kr)',
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    background: `rgba(${h.rgb.join(',')}, 0.92)`,
                    border: '1px solid rgba(255,255,255,0.85)',
                    pointerEvents: 'none',
                    transform: 'translateY(-12px)',
                  }
            }
          >
            {h.label}
          </div>
        ))}
      </model-viewer>
      <button
        type="button"
        onClick={() => setGhost((v) => !v)}
        style={{ ...pill(ghost), position: 'absolute', right: 12, top: 12 }}
      >
        잎 반투명 {ghost ? '켬' : '끔'}
      </button>
      {views.length > 1 && (
        /* 보기 고르개 — 같은 포기를 초장(줄기 경로) / 엽면적(잎 판) 으로 */
        <div
          role="group"
          aria-label="3D 보기"
          style={{
            position: 'absolute',
            left: '50%',
            top: 12,
            transform: 'translateX(-50%)',
            display: 'flex',
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid var(--md-sys-color-outline-variant)',
          }}
        >
          {views.map((v) => {
            const on = v.key === viewKey;
            return (
              <button
                key={v.key}
                type="button"
                aria-pressed={on}
                onClick={() => onViewChange(v.key)}
                style={{ ...pill(on), padding: '5px 14px', borderRadius: 0, border: 'none' }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      )}
      <span
        style={{
          position: 'absolute',
          left: 12,
          bottom: 10,
          padding: '4px 9px',
          borderRadius: 8,
          background: 'var(--md-sys-color-surface-container-highest)',
          color: 'var(--md-sys-color-on-surface-variant)',
          font: '500 10px/13px var(--gs-font-kr)',
        }}
      >
        {switching ? '보기 불러오는 중…' : '드래그하여 회전 · 스크롤하여 확대'}
      </span>
    </div>
  );
}

/* ---- 요약 줄 ----------------------------------------------------------- */

const CONFIDENCE_BG: Record<string, string> = {
  HIGH: 'rgb(38,150,140)',
  MEDIUM: 'rgb(214,160,40)',
  LOW: 'rgb(230,120,40)',
};

/** 초장 mm · 마디 구간 · QC 주의 · 엽면적 cm²(신뢰도) */
function Summary({ result }: { result: Growth3dResult }): React.ReactElement {
  const h = result.height;
  const stem = result.viewer?.stem_summary;
  const segments = h.segments_mm ?? stem?.segments_mm ?? [];
  const warnings = h.warnings ?? stem?.warnings ?? [];
  const stemColor = stem ? rgb(stem.rgb) : 'inherit';
  const leaf = result.leaf_area;
  const small: React.CSSProperties = { font: '500 12px/20px var(--gs-font-kr)', opacity: 0.85 };
  const badge = (bg: string, fg: string): React.CSSProperties => ({
    font: '700 11px/18px var(--gs-font-kr)',
    padding: '0 7px',
    borderRadius: 9999,
    background: bg,
    color: fg,
  });
  return (
    <div
      style={{
        marginTop: 10,
        padding: '9px 14px',
        borderRadius: 11,
        background: 'var(--md-sys-color-primary-container)',
        color: 'var(--md-sys-color-on-primary-container)',
        border: '1px solid var(--md-sys-color-outline-variant)',
      }}
    >
      <div
        className="gs-num"
        style={{
          font: '600 13px/20px var(--gs-font-kr)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {h.height_mm != null ? (
          <span style={{ color: stemColor }}>초장 {h.height_mm.toFixed(1)} mm</span>
        ) : (
          <span title={h.error ?? undefined}>
            초장 —{' '}
            <span style={small}>
              {h.error
                ? `3D 줄기 경로 없음 · ${h.error.length > 40 ? `${h.error.slice(0, 40)}…` : h.error}`
                : '측정값 없음'}
            </span>
          </span>
        )}
        {warnings.length > 0 && (
          /* QC 경고 — 값은 그대로, 참고로 보라는 표시 */
          <span
            title={warnings.join('\n')}
            style={badge(
              'var(--md-sys-color-error-container)',
              'var(--md-sys-color-on-error-container)',
            )}
          >
            QC 주의
          </span>
        )}
        {h.version && <span style={small}>{h.version}</span>}
      </div>
      {segments.length > 0 && (
        <div
          className="gs-num"
          style={{
            font: '500 11px/18px var(--gs-font-kr)',
            opacity: 0.85,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px 10px',
          }}
        >
          <span>마디 구간 (mm)</span>
          {segments.map((v, i) => (
            <span key={i}>
              <b style={{ color: stemColor }}>{i + 1}</b> {v.toFixed(1)}
            </span>
          ))}
        </div>
      )}
      <div
        className="gs-num"
        style={{
          font: '600 13px/20px var(--gs-font-kr)',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span>
          엽면적 {leaf?.leaf_area_cm2 != null ? `${leaf.leaf_area_cm2.toFixed(1)} cm²` : '—'}
        </span>
        {leaf?.n_compound != null && (
          <span style={small}>
            복엽 {leaf.n_compound} · 소엽 {leaf.n_leaflet ?? '—'}
          </span>
        )}
        {leaf?.confidence && (
          <span
            title={(leaf.low_reasons ?? []).join(' · ')}
            style={badge(CONFIDENCE_BG[leaf.confidence] ?? CONFIDENCE_BG.LOW, '#fff')}
          >
            신뢰도 {leaf.confidence}
            {leaf.low_share != null ? ` · 주황 ${(leaf.low_share * 100).toFixed(0)}%` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

/* ---- 촬영 사진 타임라인 -------------------------------------------------- */

/**
 * 사진이 있는 날짜를 차례로 넘겨 본다 (밀대 · 한 칸씩 · 재생).
 * 재생 중에는 사진만 바뀌고, 멈추면 그 날짜를 고른다 (3D·요약이 따라온다 — 재생마다 GLB 를 불러오지 않게).
 */
function PhotoTimeline({
  shots,
  selected,
  onPick,
  heights,
}: {
  /** 오래된 날짜부터, 사진 있는 기록만 */
  shots: GrowthRecord[];
  selected: string | null;
  onPick: (id: string) => void;
  /** 기록 id → 그날 3D 초장 (mm) */
  heights: Map<string, number | null>;
}): React.ReactElement {
  const selIdx = Math.max(
    0,
    shots.findIndex((r) => r.id === selected),
  );
  const [idx, setIdx] = useState(selIdx);
  const [playing, setPlaying] = useState(false);
  const speed = 300; // 재생 속도는 '빠르게' 하나로 고정

  // 바깥에서 날짜를 고르면 따라간다
  useEffect(() => {
    if (!playing) setIdx(selIdx);
  }, [selIdx]); // eslint-disable-line react-hooks/exhaustive-deps
  // 재생: 한 칸씩, 끝에서 멈춘다
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setIdx((i) => Math.min(i + 1, shots.length - 1)), speed);
    return () => clearInterval(t);
  }, [playing, shots.length]);
  useEffect(() => {
    if (playing && idx >= shots.length - 1) {
      const t = setTimeout(() => setPlaying(false), speed);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [playing, idx, shots.length]);
  // 멈춰 있을 때 칸이 바뀌면(밀대·한 칸) 잠깐 뒤 그 날짜를 고른다
  useEffect(() => {
    const cur = shots[idx];
    if (playing || !cur || cur.id === selected) return;
    const t = setTimeout(() => onPick(cur.id), 250);
    return () => clearTimeout(t);
  }, [idx, playing]); // eslint-disable-line react-hooks/exhaustive-deps
  // 재생이 끊기지 않게 사진을 미리 받아 둔다
  useEffect(() => {
    shots.forEach((r) => preloadPhoto(getPhotoUrl(r.photoPath!)));
  }, [shots]);

  const cur = shots[Math.min(idx, shots.length - 1)];
  const photo = useAuthedUrl(cur ? getPhotoUrl(cur.photoPath!) : null, 'photo');

  const frame: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    borderRadius: 14,
    overflow: 'hidden',
    background: 'var(--md-sys-color-surface-container)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  if (!cur)
    return (
      <div style={frame}>
        <Empty icon="no_photography" text="촬영 사진이 없습니다" />
      </div>
    );

  const step = (d: number): void => {
    setPlaying(false);
    setIdx((i) => Math.max(0, Math.min(shots.length - 1, i + d)));
  };
  const btn = (on = false): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 30,
    borderRadius: 9,
    cursor: 'pointer',
    border: '1px solid var(--md-sys-color-outline-variant)',
    background: on ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)',
    color: on ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface)',
  });
  const tag: React.CSSProperties = {
    position: 'absolute',
    top: 12,
    padding: '4px 10px',
    borderRadius: 9,
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    font: '600 12px/16px var(--gs-font-kr)',
  };
  const h = heights.get(cur.id);
  return (
    <>
      <div style={frame}>
        {photo.src ? (
          <img
            src={photo.src}
            alt={`${cur.deviceId} ${cur.recordedAt}`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Empty
            icon={photo.error ? 'error' : 'photo_camera'}
            text={photo.error ? '사진을 받지 못했습니다' : '사진 불러오는 중…'}
          />
        )}
        <span className="gs-num" style={{ ...tag, left: 12 }}>
          {dateLabel(cur.recordedAt)}
          {h != null ? ` · 초장 ${h.toFixed(1)} mm` : ''}
        </span>
        <span
          className="gs-num"
          style={{ ...tag, right: 12, padding: '4px 9px', font: '500 11px/15px var(--gs-font-kr)' }}
        >
          {idx + 1} / {shots.length}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
        <button type="button" aria-label="처음" style={btn()} onClick={() => step(-shots.length)}>
          <Sym name="first_page" size={18} />
        </button>
        <button type="button" aria-label="이전" style={btn()} onClick={() => step(-1)}>
          <Sym name="chevron_left" size={18} />
        </button>
        <button
          type="button"
          aria-label={playing ? '멈춤' : '재생'}
          aria-pressed={playing}
          style={btn(playing)}
          onClick={() => {
            if (playing) {
              setPlaying(false);
              return;
            }
            if (idx >= shots.length - 1) setIdx(0); // 끝에서 누르면 처음부터
            setPlaying(true);
          }}
        >
          <Sym name={playing ? 'pause' : 'play_arrow'} size={18} />
        </button>
        <button type="button" aria-label="다음" style={btn()} onClick={() => step(1)}>
          <Sym name="chevron_right" size={18} />
        </button>
        <button type="button" aria-label="마지막" style={btn()} onClick={() => step(shots.length)}>
          <Sym name="last_page" size={18} />
        </button>
        <input
          type="range"
          aria-label="촬영 날짜"
          min={0}
          max={shots.length - 1}
          value={idx}
          onChange={(e) => {
            setPlaying(false);
            setIdx(Number(e.target.value));
          }}
          style={{ flex: 1, minWidth: 0, accentColor: 'var(--md-sys-color-primary)' }}
        />
      </div>
      <div
        className="gs-num"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          font: '500 10px/13px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        <span>{shots[0].recordedAt.slice(5, 10).replace('-', '.')}</span>
        <span>{shots[shots.length - 1].recordedAt.slice(5, 10).replace('-', '.')}</span>
      </div>
    </>
  );
}

function Empty({ icon, text }: { icon: string; text: string }): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <Sym name={icon} size={28} style={{ color: 'var(--md-sys-color-outline)' }} />
      <span
        style={{
          font: '500 12px/16px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
        }}
      >
        {text}
      </span>
    </div>
  );
}

function dateLabel(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const w = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${w})`;
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      style={{
        height: 30,
        padding: '0 13px',
        borderRadius: 9999,
        cursor: 'pointer',
        border: active ? '0' : '1px solid var(--md-sys-color-outline-variant)',
        background: active ? 'var(--md-sys-color-secondary-container)' : 'transparent',
        color: active
          ? 'var(--md-sys-color-on-secondary-container)'
          : 'var(--md-sys-color-on-surface-variant)',
        font: `${active ? 600 : 500} 12px/1 var(--gs-font-kr)`,
      }}
    >
      {children}
    </button>
  );
}

/* ---- 촬영 기록 칸 ------------------------------------------------------ */

/** 상태 배지 — 3D 결과 json 기준. QC 경고가 있으면 '확인 필요' */
function shotStatus(
  result: Growth3dResult | null | undefined,
  loading: boolean,
  failed: boolean,
  rec?: GrowthRecord,
): { label: string; bg: string; fg: string } {
  if (loading) {
    return {
      label: '확인 중',
      bg: 'var(--md-sys-color-surface-container-high)',
      fg: 'var(--md-sys-color-on-surface-variant)',
    };
  }
  if (failed) {
    return {
      label: '읽기 실패',
      bg: 'var(--md-sys-color-error-container)',
      fg: 'var(--md-sys-color-on-error-container)',
    };
  }
  if (!result) {
    const phase = lrmPhase(rec);
    if (phase === 'failed') {
      return { label: lrmProgressShort(rec), bg: 'var(--md-sys-color-error-container)', fg: 'var(--md-sys-color-on-error-container)' };
    }
    if (phase === 'running' || phase === 'queued' || phase === 'publishing') {
      return { label: lrmProgressShort(rec), bg: 'var(--md-sys-color-tertiary-container)', fg: 'var(--md-sys-color-on-tertiary-container)' };
    }
    return {
      label: lrmProgressShort(rec),
      bg: 'var(--md-sys-color-surface-container-high)',
      fg: 'var(--md-sys-color-on-surface-variant)',
    };
  }
  if ((result.height.warnings ?? []).length > 0) {
    return {
      label: '확인 필요',
      bg: 'var(--gs-warning-container)',
      fg: 'var(--gs-on-warning-container)',
    };
  }
  return {
    label: '분석 완료',
    bg: 'var(--md-sys-color-primary-container)',
    fg: 'var(--md-sys-color-on-primary-container)',
  };
}

/** 날짜 하나 — 날짜 · 사진 썸네일 · 초장 · 상태 (grosync-local 촬영 기록과 같은 모양) */
function ShotCard({
  rec,
  active,
  result,
  loading,
  failed,
  onClick,
}: {
  rec: GrowthRecord;
  active: boolean;
  result: Growth3dResult | null | undefined;
  loading: boolean;
  failed: boolean;
  onClick: () => void;
}): React.ReactElement {
  const thumb = useAuthedUrl(getPhotoUrl(rec.photoPath!), 'photo');
  const st = shotStatus(result, loading, failed, rec);
  const h = result?.height.height_mm;
  return (
    <button
      data-active={active ? '1' : '0'}
      onClick={onClick}
      title={dateLabel(rec.recordedAt)}
      style={{
        flex: '0 0 auto',
        width: 112,
        cursor: 'pointer',
        textAlign: 'left',
        padding: 8,
        borderRadius: 13,
        border: active
          ? '2px solid var(--md-sys-color-primary)'
          : '1px solid var(--md-sys-color-outline-variant)',
        background: active
          ? 'var(--md-sys-color-surface-container-low)'
          : 'var(--md-sys-color-surface-container-lowest)',
      }}
    >
      <div
        className="gs-num"
        style={{
          font: '600 11px/15px var(--gs-font-kr)',
          color: active ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface)',
          marginBottom: 6,
        }}
      >
        {rec.recordedAt.slice(5, 10).replace('-', '.')}
      </div>
      <div
        style={{
          width: '100%',
          aspectRatio: '1 / 1',
          borderRadius: 9,
          overflow: 'hidden',
          background: 'var(--md-sys-color-surface-container)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {thumb.src ? (
          <img
            src={thumb.src}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Sym
            name={thumb.error ? 'no_photography' : 'photo_camera'}
            size={20}
            style={{ color: 'var(--md-sys-color-outline)' }}
          />
        )}
      </div>
      <div
        className="gs-num"
        style={{
          marginTop: 6,
          font: '600 12px/16px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        {h != null ? `${h.toFixed(1)} mm` : '—'}
      </div>
      <span
        style={{
          display: 'inline-block',
          marginTop: 4,
          padding: '2px 7px',
          borderRadius: 7,
          background: st.bg,
          color: st.fg,
          font: '500 9px/13px var(--gs-font-kr)',
        }}
      >
        {st.label}
      </span>
    </button>
  );
}

/* ---- 페이지 ------------------------------------------------------------ */

const RANGES = [7, 30, 60] as const;

export function Growth3dBody({ devices, farms = [] }: { devices: DeviceInfo[]; farms?: FarmInfo[] }): React.ReactElement {
  const [devId, setDevId] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  // 3D 보기 (초장 | 엽면적) — 날짜를 바꿔도 고른 보기는 유지한다
  const [view3d, setView3d] = useState('height');
  // 선택일 요약에서 고른 지표 — 추이 그래프가 따라간다
  const [trait, setTrait] = useState('height');
  const stripRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!devices.some((d) => d.id === devId) && devices.length > 0) setDevId(devices[0].id);
  }, [devices, devId]);

  const data = useDeviceShots(devId);
  const [range, setRange] = useState<number>(60);
  // 기간 — 마지막 촬영일부터 거꾸로 7 · 30 · 60 일
  const shots = useMemo(() => withinDays(data.shots, range), [data.shots, range]);
  const { results, states } = data;
  const temps = useDailyTemp(devId, shots);
  const [calendar, setCalendar] = useState(false);
  const [compare, setCompare] = useState(false);
  const heights = useMemo(() => {
    const m = new Map<string, number | null>();
    results.forEach((v, k) => m.set(k, v?.height.height_mm ?? null));
    return m;
  }, [results]);

  // 장비가 바뀌면 가장 최근 날짜로
  useEffect(() => {
    setSelected(shots[shots.length - 1]?.id ?? null);
  }, [devId, range, shots.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 선택한 칸이 보이도록 가로 목록을 움직인다
  useEffect(() => {
    const el = stripRef.current?.querySelector('[data-active="1"]') as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [selected]);

  const rec = shots.find((r) => r.id === selected);
  const resultQ = rec ? states.get(rec.id) : undefined;
  const result = resultQ?.data ?? null;
  const views = result?.viewer?.views ?? [];
  const activeView = views.find((v) => v.key === view3d) ?? views[0];
  const viewFile = activeView?.file ?? result?.files.height_view;
  const model = useAuthedUrl(
    rec && viewFile ? siblingUrl(rec.photoPath!, viewFile) : null,
    'model',
  );
  const downloadFile = result?.files.model;
  const [downloading, setDownloading] = useState(false);

  // 사진 올리기 — 올린 사진의 3D 결과가 생길 때까지 20 초마다 다시 읽는다 (최대 30 분)
  const queryClient = useQueryClient();
  const [upload, setUpload] = useState(false);
  const [watch, setWatch] = useState<{
    deviceId: string;
    recordId: string;
    photoPath: string;
    lrm: ManualUploadResult['lrm'];
    until: number;
  } | null>(null);
  const [pendingSelect, setPendingSelect] = useState<string | null>(null);
  const watchDone = !!watch && !!results.get(watch.recordId);
  // 처리 실패 — 기록의 ai_suggestions.lrm (서버가 LRM 작업 상태를 옮겨 둔다). 실패면 기다리지 않고 사유를 보인다
  const watchRecord = watch ? data.shots.find((r) => r.id === watch.recordId) : undefined;
  const watchLrm = (
    watchRecord?.aiSuggestions as {
      lrm?: { status?: string | null; terminal?: boolean; error?: { code: string; message: string } | null };
    } | null
  )?.lrm;
  // 미등록 장비 — LRM 이 제출을 거절했다 (status 없이 error·terminal 만 남는다). 기다리지 않고 관부 등록으로 안내
  const watchNeedsRegistration = !watchDone && !!watchRecord && !!lrmRegistrationNeeded(watchRecord);
  const watchFailed =
    !watchDone && !watchNeedsRegistration && (watchLrm?.status === 'failed' || watchLrm?.terminal)
      ? (watchLrm?.error ?? null)
      : null;
  useEffect(() => {
    if (!watch || watchDone || watchFailed || watchNeedsRegistration || watch.lrm !== 'submitting') return;
    const t = setInterval(() => {
      if (Date.now() > watch.until) {
        clearInterval(t);
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['growth-records', watch.deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['growth3d-result', watch.photoPath] });
    }, 20_000);
    return () => clearInterval(t);
  }, [watch, watchDone, watchFailed, watchNeedsRegistration, queryClient]);
  useEffect(() => {
    if (pendingSelect && shots.some((r) => r.id === pendingSelect)) {
      setSelected(pendingSelect);
      setPendingSelect(null);
    }
  }, [shots, pendingSelect]);
  const onUploaded = (r: ManualUploadResult): void => {
    setUpload(false);
    setWatch({
      deviceId: r.record.deviceId,
      recordId: r.record.id,
      photoPath: r.record.photoPath ?? '',
      lrm: r.lrm,
      until: Date.now() + 30 * 60_000,
    });
    setPendingSelect(r.record.id);
    void queryClient.invalidateQueries({ queryKey: ['growth-records', r.record.deviceId] });
    if (r.record.photoPath) {
      void queryClient.invalidateQueries({ queryKey: ['growth3d-result', r.record.photoPath] });
    }
  };

  if (devices.length === 0) return <PageError message="생육 분석 장비(모델 2)가 없습니다." />;

  const done = shots.filter((r) => results.get(r.id)).length;
  const review = shots.filter((r) => (results.get(r.id)?.height.warnings ?? []).length > 0).length;
  const modelNote = activeView?.model_note ?? '';
  const deviceName = devices.find((d) => d.id === devId)?.name || devId;

  return (
    <>
      {/* 내보내기 · 달력 · 비교 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
        <ToolBtn icon="download" onClick={() => exportCsv(deviceName, shots, results, temps)}>
          기록 내보내기
        </ToolBtn>
        <ToolBtn icon="calendar_month" onClick={() => setCalendar(true)}>
          달력으로 선택
        </ToolBtn>
        <ToolBtn icon="compare_arrows" onClick={() => setCompare(true)}>
          개체·날짜 비교
        </ToolBtn>
        <ToolBtn icon="add_photo_alternate" onClick={() => setUpload(true)}>
          사진 올리기
        </ToolBtn>
      </div>

      {/* 장비 / 기간 선택 */}
      <Panel pad={false} style={{ flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 14,
            padding: '13px 18px',
          }}
        >
          {/* 장비가 많아도 한 줄 — 농장별로 묶은 드롭다운 (옛 측정 기록 탭과 같은 것) */}
          <GrowthDeviceSelect devices={devices} farms={farms} value={devId} onChange={setDevId} />
          <div style={{ flex: 1 }} />
          <SectionLabel>기간</SectionLabel>
          <div style={{ display: 'flex', gap: 6 }}>
            {RANGES.map((r) => (
              <Pill key={r} active={r === range} onClick={() => setRange(r)}>
                {r}일
              </Pill>
            ))}
          </div>
        </div>
      </Panel>

      {/* 올린 사진이 관부 등록을 기다리면 — 측정 기록 탭과 같은 표시·버튼(기존 등록 모달) */}
      <LrmRegistrationNotice records={[...data.shots].reverse()} />  {/* 최신 사진부터 · 등록이 필요 없으면 아무것도 안 그린다 */}
      {watch && !watchNeedsRegistration && (
        <div
          role="status"
          style={{
            flexShrink: 0,
            padding: '10px 14px',
            borderRadius: 12,
            font: '500 13px/19px var(--gs-font-kr)',
            background: 'var(--md-sys-color-surface-container-high)',
            color: 'var(--md-sys-color-on-surface)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Sym
            name={
              watchDone
                ? 'check_circle'
                : watchFailed
                  ? 'error'
                  : watch.lrm === 'submitting'
                    ? 'hourglass_top'
                    : 'info'
            }
            size={18}
          />
          <span style={{ flex: 1 }}>
            {watchDone
              ? '3D 처리가 끝났습니다 — 아래에서 결과를 볼 수 있습니다.'
              : watchFailed
                ? `3D 처리에 실패했습니다 — ${watchFailed.message} (${watchFailed.code})`
                : watch.lrm === 'submitting'
                  ? '사진을 올렸습니다. 3D 모델링 중입니다 (보통 5~10분) — 끝나면 이 화면에 자동으로 나옵니다.'
                  : '사진을 저장했습니다. 이 서버에는 3D 처리(LRM) 연결이 없어 결과는 처리 서버가 만들면 나옵니다.'}
          </span>
          <button
            type="button"
            aria-label="닫기"
            onClick={() => setWatch(null)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
            }}
          >
            <Sym name="close" size={16} />
          </button>
        </div>
      )}


      {data.isLoading ? (
        <LoadingSpinner />
      ) : data.isError ? (
        <PageError message="생육 기록을 불러오지 못했습니다." />
      ) : (
        <>
          {/* 촬영 기록 — 날짜별 사진 · 초장 · 상태 */}
          <Panel
            title="촬영 기록"
            subtitle={`${shots.length}일 촬영 · 3D 처리 ${done}일${review ? ` · 확인 필요 ${review}건` : ''}`}
            style={{ flexShrink: 0 }}
          >
            {shots.length === 0 ? (
              <Empty icon="no_photography" text="이 장비의 촬영 사진이 없습니다" />
            ) : (
              <div
                ref={stripRef}
                style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}
              >
                {shots.map((r) => (
                  <ShotCard
                    key={r.id}
                    rec={r}
                    active={r.id === selected}
                    result={states.get(r.id)?.data}
                    loading={!!states.get(r.id)?.isLoading}
                    failed={!!states.get(r.id)?.isError}
                    onClick={() => setSelected(r.id)}
                  />
                ))}
              </div>
            )}
          </Panel>

          {/* 사진 + 3D — 두 칸은 같은 크기·같은 위치 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
              gap: 18,
              flexShrink: 0,
            }}
          >
            <Panel title="촬영 사진" subtitle={`타임라인 · ${shots.length}장`}>
              <PhotoTimeline
                shots={shots}
                selected={selected}
                onPick={setSelected}
                heights={heights}
              />
            </Panel>

            <Panel
              title="3D 생육 모델"
              subtitle={
                // 한 줄로 — 설명이 두 줄로 접히면 3D 칸이 옆 촬영 사진 칸보다 내려가 두 칸 위치가 어긋난다. 전체 문구는 마우스 올리면
                <span
                  title={modelNote}
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {result ? modelNote || '—' : resultQ?.isLoading ? '확인 중…' : lrmProgressLabel(rec)}
                </span>
              }
              action={
                rec && downloadFile ? (
                  <button
                    type="button"
                    disabled={downloading}
                    onClick={() => {
                      setDownloading(true);
                      downloadAuthed(siblingUrl(rec.photoPath!, downloadFile), downloadFile)
                        .catch((e: Error) => window.alert(`GLB 다운로드 실패: ${e.message}`))
                        .finally(() => setDownloading(false));
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      height: 30,
                      padding: '0 12px',
                      borderRadius: 9999,
                      cursor: downloading ? 'progress' : 'pointer',
                      background: 'transparent',
                      border: '1px solid var(--md-sys-color-outline-variant)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      font: '500 12px/1 var(--gs-font-kr)',
                      flexShrink: 0,
                    }}
                  >
                    <Sym name="download" size={15} />
                    {downloading ? '받는 중…' : 'GLB'}
                  </button>
                ) : undefined
              }
            >
              <ModelStage
                src={model.src}
                switching={model.pending}
                loadError={
                  model.error ?? (resultQ?.isError ? (resultQ.error?.message ?? '읽기 실패') : null)
                }
                processed={!!result || !!resultQ?.isLoading || !!resultQ?.isError}
                pendingText={lrmProgressLabel(rec)}
                views={views}
                viewKey={activeView?.key}
                onViewChange={setView3d}
                label={`${rec?.deviceId ?? ''} 3D 모델`}
                cameraOrbit={result?.viewer?.camera_orbit}
                cameraTarget={result?.viewer?.camera_target}
                fieldOfView={result?.viewer?.field_of_view}
                measureHotspots={activeView?.measure_hotspots ?? []}
                ghostDefault={!!activeView?.ghost_default}
                ghostAlpha={activeView?.ghost_alpha ?? 0.38}
              />
              {result && <Summary result={result} />}
            </Panel>
          </div>

          {shots.length > 0 && (
            <Growth3dTrends
              tempByDate={temps}
              deviceName={deviceName}
              shots={shots}
              results={results}
              selected={selected}
              trait={trait}
              onTrait={setTrait}
            />
          )}
        </>
      )}

      {calendar && (
        <CalendarPopup
          shots={shots}
          results={results}
          selected={selected}
          onPick={setSelected}
          onClose={() => setCalendar(false)}
        />
      )}
      {upload && (
        <UploadPhotoPopup
          deviceId={devId}
          deviceName={deviceName}
          onUploaded={onUploaded}
          onClose={() => setUpload(false)}
        />
      )}
      {compare && (
        <ComparePanel
          devices={devices}
          deviceId={devId}
          shots={shots}
          selected={selected}
          onClose={() => setCompare(false)}
        />
      )}
    </>
  );
}
